import React from "react";
import { motion } from "framer-motion";
import { Linkedin, MapPin, Briefcase } from "lucide-react";
import { SECTION } from "@/constants/testIds";
import { useI18n } from "@/i18n/I18nContext";

const PHOTO_URL =
  "https://customer-assets.emergentagent.com/job_birrou-advisory/artifacts/4s7r6xpu_Photo%20pro%20prof.jpg";

export default function About() {
  const { t } = useI18n();
  const a = t.about;

  return (
    <section id="about" data-testid={SECTION.about} className="section-y bg-white relative">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-6 rounded-3xl bg-gradient-to-br from-cyan-100/60 via-white to-amber-100/40 -z-10" />
              <div className="relative rounded-3xl overflow-hidden border border-slate-200 bg-slate-50 aspect-[4/5]">
                <img
                  src={PHOTO_URL}
                  alt={`${a.name} — ${a.role}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-navy/85 to-transparent">
                  <div className="text-white font-display text-lg font-semibold">{a.name}</div>
                  <div className="text-cyan-tech text-xs uppercase tracking-[0.18em] mt-0.5">
                    {a.role}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="text-overline">{a.overline}</span>
            <h2 className="mt-4 font-display font-semibold text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              {a.name}
            </h2>
            <p className="mt-6 text-slate-700 leading-relaxed text-lg">{a.p1}</p>
            <p className="mt-4 text-slate-700 leading-relaxed">{a.p2}</p>
            <p className="mt-4 text-slate-700 leading-relaxed font-medium">{a.p3}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-slate-50 text-sm text-slate-700">
                <Briefcase className="h-4 w-4 text-navy" /> {a.badgeCompanies}
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-slate-50 text-sm text-slate-700">
                <MapPin className="h-4 w-4 text-navy" /> {a.badgeRegions}
              </div>
            </div>

            <a
              href="https://linkedin.com/in/diallobirrou"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="about-linkedin-link"
              className="mt-8 btn-outline"
            >
              <Linkedin className="h-4 w-4" /> {a.linkedinCta}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
